import React, { useState } from "react"
import {
  Checkbox, Button, Tooltip, Input,
} from "antd"
import { PRIMARY_BTN_STYLE, flattenGonSelStandards, swapConnectionWord } from "./constants"
import ConnectionTypeToggle from "./ConnectionTypeToggle"

const SOURCE_TOOLTIPS = {
  document:
    "This indicator, its connection type, and explanation were found explicitly stated in your uploaded document.",
  ai: "Your document didn't name this indicator — Rei inferred it as a good fit based on the activity's overall name and description.",
}

// Recommended codes render as description cards (competency name, Promotes/Reinforces badge,
// and Rei's explanation) so the reasoning behind each pick is visible, not just a bare code.
// Cards are click-to-toggle rather than checkboxes since the explanation text is the primary
// content and a checkbox would visually compete with it. Codes outside the recommendation stay
// a plain checklist behind "+ Add another indicator" — there's no AI explanation to show for
// those, so a card would be an empty shell.
const ChecklistQuestion = ({ wizardMeta, answered, onRespond }) => {
  const { aiStandards, documentConnectionExplanation, documentSelIndicator } = wizardMeta
  const catalog = flattenGonSelStandards()
  const aiCodes = Object.keys(aiStandards || {})
  const otherCodes = Object.keys(catalog)
    .filter((code) => !aiCodes.includes(code))
    .sort()

  const [selectedCodes, setSelectedCodes] = useState(aiCodes)
  const [showMore, setShowMore] = useState(false)
  const [connectionTypes, setConnectionTypes] = useState(() => aiCodes.reduce((acc, code) => {
    acc[code] = (aiStandards[code] && aiStandards[code].connection_type) || "promotes"
    return acc
  }, {}))
  // Seeded from the document's own verbatim text when THIS is the primary document-named
  // indicator (code === documentSelIndicator) — Rei's connection_explanation for that same code
  // can be a paraphrase rather than the raw column text (see the extraction prompt), and
  // defaulting to the paraphrase silently substituted Rei's rewrite for what the user actually
  // typed. Every other code — an "ai"-sourced one, or another code the document names ALONGSIDE
  // the primary in the same cell (e.g. "1D, 1A, 4C") that Rei has independently reviewed — uses
  // its own connection_explanation directly instead: documentConnectionExplanation is the SHARED
  // row-level text tied to the primary code specifically, not a generic fallback for any
  // "document"-sourced code, so applying it here would show the wrong code's text. Editing one
  // just overrides that starting value, same as connectionTypes does for connection_type.
  const [explanations, setExplanations] = useState(() => aiCodes.reduce((acc, code) => {
    const info = aiStandards[code] || {}
    const isPrimary = code === documentSelIndicator
    acc[code] = (isPrimary && info.connection_explanation && documentConnectionExplanation)
      || info.connection_explanation || ""
    return acc
  }, {}))

  const toggleCard = (code) => {
    if (answered) return
    setSelectedCodes((prev) => (
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    ))
  }

  const setConnectionType = (code, type, e) => {
    e.stopPropagation()
    if (answered) return
    setConnectionTypes((prev) => ({ ...prev, [code]: type }))
    setExplanations((prev) => ({ ...prev, [code]: swapConnectionWord(prev[code], type) }))
  }

  const setExplanation = (code, value) => {
    setExplanations((prev) => ({ ...prev, [code]: value }))
  }

  const handleConfirm = () => {
    onRespond({
      selectedCodes,
      sel_standards: selectedCodes.reduce((acc, code) => {
        const base = (aiStandards && aiStandards[code]) || {
          connection_type: "promotes",
          connection_explanation: "",
        }
        const hasEdit = explanations[code] !== undefined
        const explanation = hasEdit
          ? explanations[code]
          : base.connection_explanation
        acc[code] = {
          ...base,
          connection_type: connectionTypes[code] || base.connection_type,
          connection_explanation: explanation,
        }
        return acc
      }, {}),
    })
  }

  return (
    <div className="wizard-checklist" style={{ marginTop: 8 }}>
      {aiCodes.map((code) => {
        const info = aiStandards[code] || {}
        const selected = selectedCodes.includes(code)
        const connectionType = connectionTypes[code] || info.connection_type || "promotes"
        const isPrimaryDocumentCode = info.source === "document" && code === documentSelIndicator
        // The editable box below defaults to the document's own verbatim text, so this is only
        // worth surfacing when Rei's own connection_explanation actually diverges from it —
        // otherwise it's the same sentence twice. Scoped to the primary code only — see the
        // explanations seed above for why documentConnectionExplanation doesn't apply elsewhere.
        const showAiSuggestion = isPrimaryDocumentCode
          && documentConnectionExplanation
          && info.connection_explanation
          && documentConnectionExplanation !== info.connection_explanation
        // The other side of showAiSuggestion: when Rei's explanation IS the document's own
        // text verbatim, that's Rei having reviewed it and found nothing to improve — worth
        // saying explicitly, since a bare "From your document" badge alone reads as "this is
        // just your own text" rather than "Rei looked at this and agrees it's already good".
        const reiConfirmedDocument = isPrimaryDocumentCode
          && documentConnectionExplanation
          && documentConnectionExplanation === info.connection_explanation
        // A different document-named code (not the primary) that Rei has independently reviewed
        // and agreed fits — enrich_sel_standards_with_ai_review flips transcribed to false only
        // when it agrees, so this is never true for one it hasn't reviewed or disagreed with.
        const reiAgreesWithDocumentIndicator = info.source === "document"
          && !info.transcribed
          && code !== documentSelIndicator
          && !!info.connection_explanation
        return (
          <div
            key={code}
            role="button"
            tabIndex={0}
            onClick={() => toggleCard(code)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleCard(code)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              cursor: answered ? "default" : "pointer",
              background: selected ? "#f4f8f7" : "#fff",
              border: `1px solid ${selected ? "#2f9e8f" : "#e2e2e2"}`,
              opacity: selected ? 1 : 0.5,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 8,
            }}
          >
            <div style={{ marginBottom: 4 }}>
              <strong>
                {`${code}. ${catalog[code] ? catalog[code].category : ""}`}
              </strong>
              {/* The category alone (e.g. "Self-Awareness") is shared by several codes and
                  doesn't say what THIS specific code means — without the actual definition, a
                  wrong suggestion (e.g. a code about personal responsibility attached to text
                  about naming emotions) isn't visibly wrong at all. */}
              {catalog[code] && catalog[code].label && (
                <div style={{ fontSize: 12, color: "#5a6a7a", marginTop: 2 }}>
                  {catalog[code].label}
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
                flexWrap: "wrap",
              }}
            >
              <ConnectionTypeToggle
                code={code}
                value={connectionType}
                disabled={answered}
                onChange={setConnectionType}
              />
              <Tooltip
                title={
                  SOURCE_TOOLTIPS[
                    info.source === "document" ? "document" : "ai"
                  ]
                }
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#5a6a7a",
                    background: "#eef1f3",
                    borderRadius: 4,
                    padding: "2px 7px",
                    cursor: "help",
                  }}
                >
                  {info.source === "document"
                    ? "From your document"
                    : "Rei recommended"}
                </span>
              </Tooltip>
            </div>
            {showAiSuggestion && (
              <div style={{ fontSize: 12, color: "#5a6a7a", marginBottom: 6 }}>
                <strong>{"Rei suggests: "}</strong>
                {`"${info.connection_explanation}"`}
              </div>
            )}
            {reiConfirmedDocument && (
              <div style={{ fontSize: 12, color: "#2f9e8f", marginBottom: 6 }}>
                ✓ Rei reviewed this and agrees it&apos;s clear
              </div>
            )}
            {reiAgreesWithDocumentIndicator && (
              <div style={{ fontSize: 12, color: "#2f9e8f", marginBottom: 6 }}>
                {"✓ Rei also reviewed this indicator and agrees it fits: "}
                {`"${info.connection_explanation}"`}
              </div>
            )}
            <Input.TextArea
              value={explanations[code] || ""}
              onChange={(e) => setExplanation(code, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={answered}
              placeholder="Describe how this activity promotes or reinforces this indicator..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontSize: 13, marginTop: "12px" }}
            />
          </div>
        )
      })}

      {otherCodes.length > 0 && !answered && !showMore && (
        <Button
          type="link"
          style={{ padding: 0, height: "auto" }}
          onClick={() => setShowMore(true)}
        >
          + Add another indicator
        </Button>
      )}

      {otherCodes.length > 0 && showMore && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginTop: 6,
            marginBottom: 6,
          }}
        >
          {otherCodes.map((code) => (
            <div key={code}>
              <Checkbox
                checked={selectedCodes.includes(code)}
                onChange={() => toggleCard(code)}
                disabled={answered}
              >
                <strong>{code}</strong>
                {catalog[code] && `. ${catalog[code].label}`}
              </Checkbox>
              {/* Always its own line below the definition, regardless of text length — sharing
                  a flex row with the checkbox let a short definition (e.g. "1D. Students have a
                  sense of personal responsibility.") keep the toggle inline on the same line
                  while a longer one wrapped it below, so the toggle's position shifted per item
                  instead of staying in a fixed, predictable place. */}
              <div style={{ marginTop: 4 }}>
                <ConnectionTypeToggle
                  code={code}
                  value={connectionTypes[code] || "promotes"}
                  disabled={answered}
                  onChange={setConnectionType}
                />
              </div>
              {selectedCodes.includes(code) && (
                <Input.TextArea
                  value={explanations[code] || ""}
                  onChange={(e) => setExplanation(code, e.target.value)}
                  disabled={answered}
                  placeholder="Describe how this activity promotes or reinforces this indicator..."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ marginTop: 4 }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {!answered && (
        <Button
          style={{ ...PRIMARY_BTN_STYLE, marginTop: 10 }}
          onClick={handleConfirm}
          disabled={selectedCodes.length === 0}
        >
          Confirm indicators
        </Button>
      )}
    </div>
  )
}

export default ChecklistQuestion
